<template>
    <b-container fluid>
        <h1>{{ pageTitle }}</h1>
        <div class="col-xs-12 col-md-10 col-lg-3">
            <b-form
                name="dataImportForm"
                v-on:submit.prevent="$_onSubmit"
                class="pb-5"
            >
                <!-- Upload -->
                <label class="d-block">{{
                    $t('dataImport.labelFileType')
                }}</label>
                <x-select
                    id="fileType"
                    name="fileType"
                    v-model="fileType"
                    :options="fileTypes"
                    :simpleMode="true"
                >
                </x-select>
                <div v-if="fileType">
                    <b-form-group
                        id="fieldset-1"
                        :label="uploadLabel"
                        :label-for="name"
                    >
                        <x-upload-stream
                            :name="name"
                            :maxSize="524288000"
                            maxSizeText="500MB"
                            :fileName="selectedFile?.name"
                            :accept="$_acceptedExtension()"
                            :acceptedExtensions="acceptedExtensions"
                            @selected="$_onFileSelected"
                        />
                    </b-form-group>
                    <b-progress
                        v-if="isUploading"
                        :value="progress"
                        max="100"
                        show-progress
                        animated
                    >
                        <b-progress-bar :value="progress" variant="success">
                            {{ progress }}%
                        </b-progress-bar>
                    </b-progress>
                    <submit-cancel-remove
                        save-label="dataImport.btnUploadLabel"
                        :is-saving="isUploading"
                        :is-btn-save-disabled="isBtnSaveDisabled"
                        :is-disabled="false"
                        @cancel="$_onCancel"
                    >
                    </submit-cancel-remove>
                </div>
            </b-form>
        </div>
        <data-import-overview
            v-if="fileType"
            :key="reloadNumber"
            @onDetails="$_onDetails"
            :format="this.fileType.toLowerCase()"
        />
        <data-import-detail-modal
            :id="dataImportId"
            @modal-hidden="$_modalHidden()"
        >
        </data-import-detail-modal>
    </b-container>
</template>

<script>
    import { nprogress } from '../../services/utilities';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import XUploadStream from '../../components/BaseComponents/x-upload-stream.vue';
    import { validateSqlName } from '../../services/utilities/validator';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import DataImportOverview from './DataImportOverview.vue';
    import DataImportDetailModal from './DataImportDetailModal.vue';
    import { DATAIMPORT_FORMAT } from '../../constants';

    export default {
        mixins: [modalMixin],
        props: { user: { type: String, default: 'unknown' } },
        components: {
            'x-select': XSelect,
            'x-upload-stream': XUploadStream,
            'submit-cancel-remove': SubmitCancelRemove,
            'data-import-overview': DataImportOverview,
            'data-import-detail-modal': DataImportDetailModal
        },

        data() {
            return {
                isUploading: false,
                name: 'DataImport',
                fileName: null,
                tableNames: [],
                tableName: null,
                tableOption: 'E',
                nameMessage: null,
                fileType: '',
                fileTypes: ['Shape', 'Geopackage', 'CSV', 'DXF'],
                reloadNumber: 0,
                dataImportId: 0,
                selectedFile: null, // will store the File object
                progress: 0,
                chunkSize: 50 * 1024 * 1024 // 50 MB
            };
        },

        async mounted() {
            this.$_loadServicesLayers();
        },

        computed: {
            pageTitle() {
                return this.$t('dataImport.pageTitle');
            },
            // Save button disabled when
            //  uploading
            //  no selectedFile
            isBtnSaveDisabled() {
                if (this.isUploading) {
                    return true;
                }
                return !this.selectedFile;
            },

            uploadLabel() {
                return this.$t('dataImport.uploadLabel', [
                    this.fileType,
                    this.$_acceptedExtension()
                ]);
            },

            acceptedExtensions() {
                if (this.$_hasFormat(this.fileType, DATAIMPORT_FORMAT.SHAPE)) {
                    return ['zip'];
                } else if (this.$_hasFormat(this.fileType, DATAIMPORT_FORMAT.CSV)) {
                    return ['csv'];
                } else if (this.$_hasFormat(this.fileType, DATAIMPORT_FORMAT.DXF)) {
                    return ['dxf'];
                } else {
                    return ['gpkg'];
                }
            }
        },

        methods: {
            $_onInputName(newValue) {
                this.nameMessage = null;
                const found = this.tableNames.find((item) => {
                    return item.toLowerCase() === newValue.toLowerCase();
                });

                if (found !== undefined) {
                    this.nameMessage = this.$t('dataImport.nameIsInUse');
                }

                if (!validateSqlName(newValue)) {
                    this.nameMessage = this.$t('errorInvalidColumnName');
                }

                this.tableName = newValue;
            },

            // Compare lowecase
            $_hasFormat(fileType, format) {
                return fileType.toLowerCase() === format;
            },

            $_setTableOption(newVal) {
                this.tableOption = newVal;
                this.tableName = null;
                this.nameMessage = null;
            },
            $_acceptedExtension() {
                if (this.fileType === 'Shape') {
                    return '.zip';
                } else if (this.fileType === 'CSV') {
                    return '.csv';
                } else if (this.fileType === 'DXF') {
                    return '.dxf';
                } else {
                    return '.gpkg';
                }
            },
            async $_onTableSelected(value) {
                this.tableName = value;
            },
            async $_loadServicesLayers() {
                if (this.tableNames.length === 0) {
                    const tableNamesResult = await this.$store.dispatch(
                        'useApiData',
                        {
                            name: 'GetTableNames',
                            parameters: { geometrytables: true }
                        }
                    );
                    this.tableNames = tableNamesResult.data;
                }
            },

            $_onFileSelected(file) {
                // Receive the raw File object from the child
                this.selectedFile = file;
            },

            $_onSubmit() {
                let text = this.$t('dataImport.confirmation');
                this.msgBoxConfirm({ message: text }).then((confirmed) => {
                    if (confirmed) {
                        this.$_submit();
                    }
                });
            },

            async $_submit() {
                if (!this.selectedFile) {
                    this.showToast({
                        message: 'Please select a file first.',
                        variant: 'danger'
                    });
                    return;
                }
                this.isUploading = true;
                nprogress.start();
                try {
                    const totalChunks = Math.ceil(
                        this.selectedFile.size / this.chunkSize
                    );
                    const fileId = crypto.randomUUID();

                    for (let index = 0; index < totalChunks; index++) {
                        const start = index * this.chunkSize;
                        const end = Math.min(
                            start + this.chunkSize,
                            this.selectedFile.size
                        );
                        const chunk = this.selectedFile.slice(start, end);

                        const formData = new FormData();
                        formData.append('FileId', fileId);
                        formData.append('ChunkIndex', index);
                        formData.append('Chunk', chunk);
                        await this.$store.dispatch('useApiData', {
                            name: 'DataFileImportChunk', // matches operationId in swagger
                            data: formData,
                            config: {
                                timeout: 0, // no timeout for large uploads
                                onUploadProgress: (e) => {
                                    // progress for this single chunk
                                    const chunkProgress = e.loaded / e.total;

                                    // overall progress = (chunks before this one + current chunk fraction) / total
                                    const overallProgress =
                                        ((index + chunkProgress) / totalChunks) *
                                        100;

                                    this.progress = Math.min(
                                        100,
                                        Math.round(overallProgress)
                                    );
                                }
                            }
                        });
                    }

                    const result = await this.$store.dispatch('useApiData', {
                        name: 'DataFileImportComplete',
                        data: {
                            fileId: fileId,
                            fileName: this.selectedFile.name,
                            tableName: this.tableName ?? '',
                            totalChunks: totalChunks,
                            format: this.fileType.toLowerCase()
                        }
                    });

                    // Load dataImportId to open modal screen
                    this.dataImportId = parseInt(result.data);
                    this.reloadNumber++;
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                } finally {
                    nprogress.done();
                    this.isUploading = false;
                    this.fileName = null;
                    this.tableName = null;
                    this.selectedFile = null; // reset after upload
                }
            },
            $_onCancel() {
                this.$router.push({ path: '/' });
            },
            $_modalHidden() {
                this.dataImportId = 0;
                this.reloadNumber++;
            },
            $_onDetails(id) {
                this.dataImportId = id;
            }
        }
    };
</script>
